import {
    EditorCore,
    EditorPlugin,
    PluginEvent,
    PluginEventType,
    TriggerEvent,
} from 'roosterjs-editor-types';
import { logUsage } from 'owa-analytics';

const enum LoggingStage {
    /**
     * Stage 1: measure whole time for a full key down ... key up event cycle.
     * Each keyboard event cycle will have only 1 measure.
     * Group measures for 10 cycles
     */
    Stage1,

    /**
     * Stage 2: measure whole time for each separate keyboard event for all plugin
     * Each keyboard event cycle will have 4 measures (keydown, keypress, input, keyup).
     * Group measures for 10 cycles
     */
    Stage2,

    /**
     * Stage 3: measure time for each separate keyboard event and each plugin.
     * Each keyboard event cycle will have 4*N measures (N=Plugin count).
     * Group measure for 200 events
     */
    Stage3,

    /**
     * Stage 4: No measure since we have already logged perf issue
     */
    Stage4,
}

interface PerfLoggingData {
    /**
     * Current logging stage
     */
    stage: LoggingStage;

    /**
     * How many times that perf measure time threshold has been met in current stage
     */
    meetThresholdCount: number;

    /**
     * Marked marker indexes and related event types, plugin names.
     * An event is marked in pair, we store start and end index here.
     * So later we can measure the time duration using the marker names generated from these indexes.
     * And if perf issue found, we can log with its related event type and plugin name.
     */
    markerIndexes: {
        startIndex: number;
        endIndex: number;
        eventType: PluginEventType;
        pluginName: string;
    }[];
}

/**
 * Custom data key to store the perf data in editor.
 * Editor will manage the lifecycle of the data and call our disposer function when editor is disposing
 */
const PerfLogDataKey = 'OWAPerfLogCustomData';

/**
 * Prefix of perf marker.
 * A perf marker is in the format of:
 * roosterjsMarker_{editorId}_{index}
 */
const MarkerNamePrefix = 'roosterjsMarker_';

/**
 * Name prefix of measure.
 * A perf measure name is in the format of
 * roosterjsMeasure_{editorId}
 */
const MeasureNamePrefix = 'roosterjsMeasure_';

/**
 * Event name for logging
 */
const LogEventName = 'RoosterJsPerfMeasure';

/**
 * Necessary inforamtion for a stage
 */
interface StageInfo {
    /**
     * The time threshold of this stage. When enough violations to this time are found in one measure group,
     * we will either increase current meetThresholdCount or stage
     */
    timeThreshold: number;

    /**
     * Min count of violations in one measure group. A violation means the actual time is greater than timeThreshold
     */
    minViolationCount: number;

    /**
     * Size of measure group. We mark time for a bunch of event handlers, but only when the mark count reaches this
     * group size then we will do a bulk measure together in order to reduce the chance of measure and avoid impacting
     * perf from the measure itself
     */
    measureGroupSize: number;

    /**
     * Count of test in each stage. Only when we found enough meetThresholdCount in one stage, we will move to next stage
     */
    repeatTestCount: number;

    /**
     * Handler (triggerEvent) of this stage
     */
    handler: (
        core: EditorCore,
        editorId: string,
        pluginEvent: PluginEvent,
        data: PerfLoggingData
    ) => void;
}

/**
 * Information of each stage
 */
const StageInfoSet: {
    [stage in LoggingStage]: StageInfo;
} = {
    [LoggingStage.Stage1]: {
        timeThreshold: 150,
        measureGroupSize: 10,
        repeatTestCount: 4,
        minViolationCount: 5,
        handler: (core, editorId, pluginEvent, data) => {
            // Stage 1: mark start when key down
            if (pluginEvent.eventType == PluginEventType.KeyDown) {
                markStart(data, editorId);
            }
            core.plugins.forEach(plugin => plugin.onPluginEvent?.(pluginEvent));

            // Stage 1: mark end when key up
            if (pluginEvent.eventType == PluginEventType.KeyUp) {
                markEnd(data, editorId);
            }
        },
    },
    [LoggingStage.Stage2]: {
        timeThreshold: 100,
        measureGroupSize: 40,
        repeatTestCount: 2,
        minViolationCount: 2,
        handler: (core, editorId, pluginEvent, data) => {
            // Stage 2: mark before and after each keyboard event
            markStart(data, editorId, pluginEvent.eventType);
            core.plugins.forEach(plugin => plugin.onPluginEvent?.(pluginEvent));
            markEnd(data, editorId);
        },
    },
    [LoggingStage.Stage3]: {
        timeThreshold: 50,
        measureGroupSize: 200,
        repeatTestCount: 1,
        minViolationCount: 1,
        handler: (core, editorId, pluginEvent, data) => {
            // Stage 3: mark for each plugin
            core.plugins.forEach(plugin => {
                if (plugin.onPluginEvent) {
                    markStart(data, editorId, pluginEvent.eventType, plugin.getName());
                    plugin.onPluginEvent(pluginEvent);
                    markEnd(data, editorId);
                }
            });
        },
    },
    [LoggingStage.Stage4]: {
        timeThreshold: Number.MAX_VALUE,
        measureGroupSize: Number.MAX_VALUE,
        repeatTestCount: Number.MAX_VALUE,
        minViolationCount: Number.MAX_VALUE,
        handler: (core, editorId, pluginEvent) => {
            // Stage 4: no mark, no logging
            core.plugins.forEach(plugin => plugin.onPluginEvent?.(pluginEvent));
        },
    },
};

/**
 * A map from keyboard event index to a friendly name to make the log readable
 */
const EventNameMap: { [key in PluginEventType]?: string } = {
    [PluginEventType.KeyDown]: 'keydown',
    [PluginEventType.KeyPress]: 'keypress',
    [PluginEventType.Input]: 'input',
    [PluginEventType.KeyUp]: 'keyup',
};

export default function getTriggerEventAPIWithMeasure(editorId: string): TriggerEvent {
    return (core: EditorCore, pluginEvent: PluginEvent, broadcast: boolean) => {
        if (
            !core.lifecycle.shadowEditFragment &&
            (broadcast || !core.plugins.some(plugin => handledExclusively(pluginEvent, plugin)))
        ) {
            const eventType = pluginEvent.eventType;
            const isKeyboardEvent =
                eventType == PluginEventType.KeyDown ||
                eventType == PluginEventType.KeyPress ||
                eventType == PluginEventType.Input ||
                eventType == PluginEventType.KeyUp;
            const loggingData = getCustomDataForLogging(core, editorId);

            // For events other than keyboard, always treat as stage 4 so we don't need to log
            const handler =
                StageInfoSet[isKeyboardEvent ? loggingData.stage : LoggingStage.Stage4].handler;

            handler(core, editorId, pluginEvent, loggingData);
        }
    };
}

function getCustomDataForLogging(core: EditorCore, editorId: string): PerfLoggingData {
    return (core.lifecycle.customData[PerfLogDataKey] = core.lifecycle.customData[
        PerfLogDataKey
    ] || {
        value: <PerfLoggingData>{
            stage: LoggingStage.Stage1,
            markerIndexes: [],
            meetThresholdCount: 0,
        },
        disposer: data => {
            cleanUp(data, editorId);
        },
    }).value;
}

function handledExclusively(event: PluginEvent, plugin: EditorPlugin): boolean {
    if (plugin.onPluginEvent && plugin.willHandleEventExclusively?.(event)) {
        plugin.onPluginEvent(event);
        return true;
    }

    return false;
}

function getMarkerName(editorId: string, index: number) {
    return MarkerNamePrefix + editorId + '_' + index;
}

function getMeasureName(editorId) {
    return MeasureNamePrefix + editorId;
}

function markStart(
    data: PerfLoggingData,
    editorId: string,
    eventType?: PluginEventType,
    pluginName?: string
) {
    const { markerIndexes } = data;
    const lastEntry = markerIndexes[markerIndexes.length - 1];
    const index = markerIndexes.length * 2;

    if (lastEntry?.endIndex < 0) {
        // End previous mark if exist.
        // This can happen if user hold a key then KeyDown/KeyPress/Input event will be triggered repeatly
        // but KeyUp event is only triggered when user release the key.
        // So for stage 1, we treat next KeyDown event as the end marker of previous KeyDown event as well
        lastEntry.endIndex = index;
    }

    markerIndexes.push({
        startIndex: index,
        endIndex: -1,
        eventType,
        pluginName,
    });

    window.performance.mark(getMarkerName(editorId, index));
}

function markEnd(data: PerfLoggingData, editorId: string) {
    const { markerIndexes } = data;
    const lastEntry = markerIndexes[markerIndexes.length - 1];
    const index = markerIndexes.length * 2 - 1;

    if (lastEntry?.endIndex < 0) {
        window.performance.mark(getMarkerName(editorId, index));
        lastEntry.endIndex = index;
    }

    const { measureGroupSize, timeThreshold, minViolationCount, repeatTestCount } = StageInfoSet[
        data.stage
    ];

    // Start measure when there are enough markers for measure
    if (markerIndexes.length >= measureGroupSize) {
        const measureName = getMeasureName(editorId);
        markerIndexes.forEach(marker => {
            window.performance.measure(
                measureName,
                getMarkerName(editorId, marker.startIndex),
                getMarkerName(editorId, marker.endIndex)
            );
        });

        const entries = window.performance.getEntriesByName(measureName);
        const filteredEntries = entries.filter(e => e.duration > timeThreshold);

        if (filteredEntries.length < minViolationCount) {
            // No perf issue found in this measure round, reduce meetThresholdCount and downgrade logging stage if necessary
            if (data.stage == LoggingStage.Stage1) {
                // We are good here, no op
            } else if (data.meetThresholdCount > 0) {
                data.meetThresholdCount--;
            } else {
                data.stage =
                    data.stage == LoggingStage.Stage3 ? LoggingStage.Stage2 : LoggingStage.Stage1;
                data.meetThresholdCount = StageInfoSet[data.stage].repeatTestCount - 1;
            }
        } else if (data.meetThresholdCount + 1 < repeatTestCount) {
            // Found perf issue, but we have not met repeat test count, increase the test count so next time it may meet the
            // repeat test count probably
            data.meetThresholdCount++;
        } else if (data.stage == LoggingStage.Stage3) {
            // Found perf issue, and we have done enough test in stage 3, need to log event, and move to stage 4 to stop more logging
            filteredEntries.forEach(e => {
                const index = entries.indexOf(e);
                const marker = markerIndexes[index];
                log(editorId, marker.eventType, marker.pluginName, e.duration);
            });
            data.stage = LoggingStage.Stage4;
        } else {
            // Found perf issue, and we have done enough test in current stage (1 or 2), move to next stage
            data.stage =
                data.stage == LoggingStage.Stage1 ? LoggingStage.Stage2 : LoggingStage.Stage3;
            data.meetThresholdCount = 0;
        }

        cleanUp(data, editorId);
    }
}

function cleanUp(data: PerfLoggingData, editorId: string) {
    window.performance.clearMeasures(getMeasureName(editorId));
    data.markerIndexes.forEach(marker => {
        window.performance.clearMarks(getMarkerName(editorId, marker.startIndex));
        window.performance.clearMarks(getMarkerName(editorId, marker.endIndex));
    });
    data.markerIndexes = [];
}

function log(editorId: string, event: PluginEventType, pluginName: string, duration: number) {
    logUsage(LogEventName, {
        editorId,
        event: EventNameMap[event],
        plugin: pluginName,
        duration,
    });
}
